import type { SxProps, Theme } from '@mui/material';

const schedulePanelFieldRootSx = {
  borderRadius: '12px',
  bgcolor: 'rgba(255,255,255,0.18)',
  color: '#fff',
  fontFamily: 'Inter, sans-serif',
};

const schedulePanelOutlineSx = {
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(181, 235, 255, 0.35)',
  },
  '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(181, 235, 255, 0.5)',
  },
  '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(199, 241, 255, 0.72)',
  },
  '& .MuiInputBase-input::placeholder': {
    color: 'rgba(226, 244, 252, 0.65)',
    opacity: 1,
  },
};

export const schedulePanelLabelSx: SxProps<Theme> = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: '12px',
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  color: 'rgba(226, 244, 252, 0.95)',
};

export const schedulePanelSectionSx: SxProps<Theme> = {
  mt: 2.25,
  display: 'flex',
  flexDirection: 'column',
  gap: 1.15,
};

export const schedulePanelGroupSx: SxProps<Theme> = {
  p: 1.35,
  borderRadius: '14px',
  bgcolor: 'rgba(255,255,255,0.08)',
  border: '1px solid rgba(181, 235, 255, 0.2)',
  display: 'flex',
  flexDirection: 'column',
  gap: 1.1,
};

export const schedulePanelInputSx: SxProps<Theme> = {
  ...schedulePanelOutlineSx,
  '& .MuiInputBase-root': {
    ...schedulePanelFieldRootSx,
    minHeight: 40,
    fontWeight: 600,
    fontSize: '0.82rem',
  },
};

export const schedulePanelTextareaSx: SxProps<Theme> = {
  ...schedulePanelOutlineSx,
  '& .MuiInputBase-root': {
    ...schedulePanelFieldRootSx,
    fontWeight: 500,
    fontSize: '0.78rem',
    alignItems: 'flex-start',
  },
};

export const schedulePanelSelectSx: SxProps<Theme> = {
  ...schedulePanelInputSx,
  '& .MuiInputBase-root': {
    ...schedulePanelFieldRootSx,
    minHeight: 40,
    fontWeight: 600,
    fontSize: '0.82rem',
    '& .MuiSvgIcon-root': {
      color: '#d9eef7',
      fontSize: 20,
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '& .MuiInputBase-root:hover': {
    bgcolor: 'rgba(255,255,255,0.24)',
  },
};

/** Light-theme field styles for the mobile schedule sheet. */
export const scheduleMobileLabelSx: SxProps<Theme> = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: '12px',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: '#667085',
};

export const scheduleMobileSectionSx: SxProps<Theme> = {
  mt: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 1.15,
};

export const scheduleMobileGroupSx: SxProps<Theme> = {
  p: 1.5,
  borderRadius: '16px',
  bgcolor: '#fff',
  border: '1px solid #E4E7EC',
  display: 'flex',
  flexDirection: 'column',
  gap: 1.25,
};

const scheduleMobileOutlineSx = {
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#E4E7EC',
  },
  '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#D0D5DD',
  },
  '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#2FA8D5',
  },
  '& .MuiInputBase-input::placeholder': {
    color: '#98A2B3',
    opacity: 1,
  },
};

export const scheduleMobileInputSx: SxProps<Theme> = {
  width: '100%',
  ...scheduleMobileOutlineSx,
  '& .MuiInputBase-root': {
    borderRadius: '12px',
    bgcolor: '#fff',
    color: '#1D2939',
    fontFamily: 'Inter, sans-serif',
    minHeight: 44,
    fontWeight: 600,
    fontSize: '0.9rem',
    border: '1px solid #E4E7EC',
    boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
  },
  '& .MuiInputBase-root:hover': {
    borderColor: '#D0D5DD',
  },
  '& .MuiInputBase-root.Mui-focused': {
    borderColor: '#2FA8D5',
    boxShadow: '0 0 0 3px rgba(47, 168, 213, 0.12)',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
};

export const scheduleMobileTextareaSx: SxProps<Theme> = {
  width: '100%',
  ...scheduleMobileOutlineSx,
  '& .MuiInputBase-root': {
    borderRadius: '12px',
    bgcolor: '#fff',
    color: '#1D2939',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 500,
    fontSize: '0.88rem',
    alignItems: 'flex-start',
    border: '1px solid #E4E7EC',
    boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
  },
  '& .MuiInputBase-root:hover': {
    borderColor: '#D0D5DD',
  },
  '& .MuiInputBase-root.Mui-focused': {
    borderColor: '#2FA8D5',
    boxShadow: '0 0 0 3px rgba(47, 168, 213, 0.12)',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
};

export const scheduleMobileSelectSx: SxProps<Theme> = {
  ...scheduleMobileInputSx,
  '& .MuiInputBase-root': {
    borderRadius: '12px',
    bgcolor: '#fff',
    color: '#1D2939',
    fontFamily: 'Inter, sans-serif',
    minHeight: 44,
    fontWeight: 600,
    fontSize: '0.9rem',
    border: '1px solid #E4E7EC',
    boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
    '& .MuiSvgIcon-root': {
      color: '#667085',
      fontSize: 22,
    },
  },
};

/** Breakpoint-aware styles so mobile layout does not depend on useMediaQuery. */
export const scheduleResponsiveLabelSx: SxProps<Theme> = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: '12px',
  letterSpacing: { xs: '0.06em', md: '0.16em' },
  textTransform: 'uppercase',
  color: { xs: '#667085', md: 'rgba(226, 244, 252, 0.95)' },
};

export const scheduleResponsiveSectionSx: SxProps<Theme> = {
  mt: { xs: 0, md: 2.25 },
  display: 'flex',
  flexDirection: 'column',
  gap: 1.15,
  width: '100%',
};

export const scheduleResponsiveGroupSx: SxProps<Theme> = {
  width: '100%',
  boxSizing: 'border-box',
  p: { xs: 1.5, md: 1.35 },
  borderRadius: { xs: '16px', md: '14px' },
  bgcolor: { xs: '#fff', md: 'rgba(255,255,255,0.08)' },
  border: { xs: '1px solid #E4E7EC', md: '1px solid rgba(181, 235, 255, 0.2)' },
  display: 'flex',
  flexDirection: 'column',
  gap: { xs: 1.25, md: 1.1 },
};

export const scheduleActionButtonSx = (enabled: boolean): SxProps<Theme> => ({
  width: '100%',
  minHeight: 50,
  borderRadius: '14px',
  textTransform: 'none',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: '1.05rem',
  boxShadow: 'none',
  backgroundColor: enabled ? '#2FA8D5' : '#EEF2F6',
  color: enabled ? '#fff' : '#667085',
  '&:hover': {
    backgroundColor: enabled ? '#249CCB' : '#EEF2F6',
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    backgroundColor: '#EEF2F6',
    color: '#667085',
    opacity: 1,
  },
});

export const scheduleActionButtonCompactSx = (enabled: boolean): SxProps<Theme> => ({
  flex: 1,
  minHeight: 48,
  borderRadius: '12px',
  textTransform: 'none',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: '0.95rem',
  boxShadow: 'none',
  backgroundColor: enabled ? '#2FA8D5' : '#EEF2F6',
  color: enabled ? '#fff' : '#667085',
  '&:hover': {
    backgroundColor: enabled ? '#249CCB' : '#EEF2F6',
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    backgroundColor: '#EEF2F6',
    color: '#667085',
    opacity: 1,
  },
});

export const scheduleCenterPanelSx: SxProps<Theme> = {
  p: 1.35,
  borderRadius: '14px',
  border: '1px solid #E4E7EC',
  bgcolor: '#F8FAFC',
};

export const scheduleCenterTitleSx: SxProps<Theme> = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: '0.9rem',
  color: '#1D2939',
  lineHeight: 1.25,
};

export const scheduleCenterSubtitleSx: SxProps<Theme> = {
  mt: 0.35,
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 600,
  color: '#98A2B3',
  lineHeight: 1.4,
};

export const scheduleCenterSearchSx: SxProps<Theme> = {
  mt: 1,
  '& .MuiInputBase-root': {
    borderRadius: '12px',
    bgcolor: '#fff',
    minHeight: 42,
    border: '1px solid #E4E7EC',
    boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  },
  '& .MuiInputBase-root:hover': {
    borderColor: '#D0D5DD',
  },
  '& .MuiInputBase-root.Mui-focused': {
    borderColor: '#2FA8D5',
    boxShadow: '0 0 0 3px rgba(47, 168, 213, 0.12)',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '& .MuiInputBase-input': {
    fontFamily: 'Inter, sans-serif',
    color: '#1D2939',
    fontSize: '0.8rem',
    py: '8px',
  },
  '& .MuiInputBase-input::placeholder': {
    color: '#98A2B3',
    opacity: 1,
  },
};

export const scheduleCenterSelectedClientSx: SxProps<Theme> = {
  mt: 1,
  px: 1.1,
  py: 0.85,
  borderRadius: '10px',
  bgcolor: '#E9F5FB',
  border: '1px solid #B8E4F7',
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  minWidth: 0,
};

export const scheduleCalendarPanelSx: SxProps<Theme> = {
  p: 1.35,
  borderRadius: '14px',
  border: '1px solid #E4E7EC',
  bgcolor: '#fff',
  boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
};

export const schedulePastDayTooltip =
  'Past dates cannot be booked. Select today or a future available date.';

export const scheduleLoadingDayTooltip = 'Checking your availability…';

export const scheduleUnavailableDayTooltip =
  'Add availability slots in Availability to enable this date for booking.';

/** Public booking / reschedule pages (client-facing). */
export const publicBookingPastDayTooltip = 'This date has passed.';
export const publicBookingUnavailableDayTooltip = 'This date is not available for booking.';
